import { VisibilityElement } from './visibility'
import { formatMoney } from '../helpers/currency-formatter'

interface ItemLocation {
  title: string
  weights: Array<{
    min_weight: number
    max_weight: number
    title: string
  }>
  prices: number[]
}

interface ItemData {
  locations: ItemLocation[]
  productWeight: number
  weightUnit: string
}

/**
 * Shipping Calculator CUSTOM ELEMENT
 *
 * Handles location selection and displays the associated price based on product weight.
 * Extends VisibilityElement for modal functionality.
 */
export class ShippingCalculator extends VisibilityElement {
  private selectElement: HTMLSelectElement | null = null
  private priceDisplayElement: HTMLElement | null = null
  private ItemData: ItemData | null = null

  static SELECTOR_KEY = '[data-role="location-select"]'
  static PRICE_DISPLAY_KEY = '[data-role="price-display"]'

  /**
   * Initialize the item data from the element's dataset
   */
  private initData(): void {
    const itemData = this.dataset.itemData
    if (itemData) {
      try {
        this.ItemData = JSON.parse(itemData)
      } catch (error) {
        console.error('Failed to parse item data:', error)
      }
    }
  }

  /**
   * Fetch child elements
   */
  private fetchChildren(): void {
    this.selectElement = this.querySelector(ShippingCalculator.SELECTOR_KEY)
    this.priceDisplayElement = this.querySelector(ShippingCalculator.PRICE_DISPLAY_KEY)
  }

  /**
   * Handle location selection change
   */
  private handleLocationChange = (event: Event): void => {
    const select = event.currentTarget as HTMLSelectElement
    const selectedLocationTitle = select.value

    if (!selectedLocationTitle || !this.ItemData) {
      this.hidePriceDisplay()
      return
    }

    const selectedLocation = this.ItemData.locations.find(
      location => location.title === selectedLocationTitle
    )

    if (selectedLocation) {
      this.displayPriceForLocation(selectedLocation)
    } else {
      this.hidePriceDisplay()
    }
  }

  /**
   * Display price for the selected location based on product weight
   */
  private displayPriceForLocation(location: ItemLocation): void {
    if (!this.priceDisplayElement || !this.ItemData) return

    const { productWeight } = this.ItemData
    let matchingPrice: number | null = null

    // Find the weight range that matches the product weight
    for (let i = 0; i < location.weights.length; i++) {
      const weightRange = location.weights[i]
      const minWeight = weightRange.min_weight
      const maxWeight = weightRange.max_weight

      if (productWeight >= minWeight && productWeight <= maxWeight) {
        matchingPrice = location.prices[i]
        break
      }
    }

    if (matchingPrice !== null) {
      // Format the price (assuming it's stored in cents)
      const formattedPrice = this.formatPrice(matchingPrice)

      this.priceDisplayElement.innerHTML = `
        <div class="flex bg-neutral-light p-1">
          <div class="paragraph-m font-cnd font-bold uppercase">
            ${location.title} Delivery - ${formattedPrice}
          </div>
        </div>
      `
      this.priceDisplayElement.classList.remove('hidden')
    } else {
      this.priceDisplayElement.innerHTML = `
        <div class="flex bg-neutral-light p-1">
          <div class="paragraph-m font-cnd font-bold uppercase">
            No shipping rates available - please <a href="/pages/contact" class="link-fade" aria-label="Contact us">contact us</a>.
          </div>
        </div>
      `
      this.priceDisplayElement.classList.remove('hidden')
    }
  }

  /**
   * Hide the price display
   */
  private hidePriceDisplay(): void {
    if (this.priceDisplayElement) {
      this.priceDisplayElement.classList.add('hidden')
    }
  }

  /**
   * Format price from cents to currency string
   */
  private formatPrice(priceInCents: number): string {
    // Get currency formatting from global state
    const moneyFormat = window.globals?.shopify?.moneyFormat || '${{amount}}'
    return formatMoney(priceInCents, moneyFormat)
  }

  /**
   * Add event listeners
   */
  addEventListeners(): void {
    super.addEventListeners()
    this.selectElement?.addEventListener('change', this.handleLocationChange)
  }

  /**
   * Remove event listeners
   */
  removeEventListeners(): void {
    super.removeEventListeners()
    this.selectElement?.removeEventListener('change', this.handleLocationChange)
  }

  /**
   * Called when the element is connected to the DOM
   */
  connectedCallback(): void {
    super.connectedCallback()
    this.initData()
    this.fetchChildren()
    this.addEventListeners()
  }

  /**
   * Called when the element is disconnected from the DOM
   */
  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListeners()
  }
}
